/**
 * Resolves when the element has non-zero clientWidth and clientHeight.
 * Resolves immediately if already non-zero.
 *
 * The resolve is scheduled in `requestAnimationFrame` to let the
 * ResizeObserver callback fully exit before consumers run side effects,
 * preventing "ResizeObserver loop completed with undelivered notifications".
 *
 * Returns an object with the promise and a `cancel` function that disconnects
 * the observer — use it in cleanup to avoid leaks if the element unmounts
 * before reaching non-zero size.
 */
export interface IWaitForElementToHaveSizeResult {
	promise: Promise<void>;
	cancel: () => void;
}

export const waitForElementToHaveSize = (
	node: HTMLElement
): IWaitForElementToHaveSizeResult => {
	const hasNonZeroSize = () => node.clientWidth > 0 && node.clientHeight > 0;

	if (hasNonZeroSize()) {
		return { promise: Promise.resolve(), cancel: () => undefined };
	}

	let observer: ResizeObserver | null = null;

	const promise = new Promise<void>((resolve) => {
		observer = new ResizeObserver(() => {
			if (hasNonZeroSize()) {
				observer?.disconnect();
				observer = null;
				requestAnimationFrame(() => resolve());
			}
		});
		observer.observe(node);
	});

	const cancel = () => {
		observer?.disconnect();
		observer = null;
	};

	return { promise, cancel };
};
